import { Button } from "components/Button/Button";
import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { TableColumn } from "components/Table/tableUtils";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import { FeRecordLod, FeRecordLogRecord } from "config/config";
import { createdAtDto } from "config/utils";
import {
    ReferenceItem,
    ReferenceListPopover,
} from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/ReferenceList";
import { useEffect, useMemo, useRef, useState } from "react";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";
import style from "./LogsListing.module.scss";

export const ExpandableData = ({ data, uniqueKey }: { data: string; uniqueKey: string }) => {
    const [isExpanded, setExpanded] = useState(false);
    const markdownRef = useRef<any>(null);
    const clipRef = useRef<HTMLDivElement>(null);
    const [canExpand, setCanExpand] = useState(false);

    const isCode = useMemo(() => {
        const trimmedData = data.trim();

        // Match strings that start and end with triple backticks
        const markdownCodeBlockRegex = /^```[a-zA-Z0-9]*\n[\s\S]*?\n?```$/;
        return markdownCodeBlockRegex.test(trimmedData);
    }, [data]);

    const trimmedData = useMemo(() => {
        if (!isCode) {
            return data;
        }
        const trimmedData = data.trim();
        return trimmedData.replace(/```\n\n```/g, "").replace(/\n+```$/, "\n```");
    }, [isCode, data]);

    useEffect(() => {
        setCanExpand(markdownRef.current?.scrollHeight - 27 > Number(style.initialColumnHeight));
    }, [data]);

    // On collapse, reset the inner scroll container to the top. The clip wrapper
    // is the scroll container while expanded (max-height + overflow-y:auto); its
    // scrollTop is retained when it is re-clipped on collapse, so the collapsed
    // row would otherwise show lines from the middle of the content instead of
    // the first line (operator-task-fe-execution-log-data-column-polish, item 1).
    useEffect(() => {
        if (!isExpanded && clipRef.current) {
            clipRef.current.scrollTop = 0;
            clipRef.current.scrollLeft = 0;
        }
    }, [isExpanded]);

    // Canonical clipped-content disclosure (ui-design-principle-show-more):
    // clip wrapper + measured inner content, a soft fade cue over the clipped
    // bottom while collapsed, and a quiet "Show more/less" text link below the
    // content (no black pill, no negative-margin hack over the row divider).
    return (
        <div className={style.expandableDataWrapper}>
            <div
                ref={clipRef}
                className={
                    canExpand && !isExpanded
                        ? `${style.clipWrap} ${style.clipWrapCollapsed}`
                        : style.clipWrap
                }
            >
                <div ref={markdownRef} className={style.contentInner}>
                    <CustomLinkMarkdown
                        markdownString={trimmedData}
                        className="statusMessageMarkdown statusMessageMarkdownColumn"
                        uniqueKey={uniqueKey}
                    />
                </div>
                {canExpand && !isExpanded && <div className={style.fadeOverlay} />}
            </div>
            {canExpand && (
                <div
                    className={style.showMoreLink}
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded((isExpanded) => !isExpanded)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setExpanded((isExpanded) => !isExpanded);
                        }
                    }}
                >
                    <Button
                        label={`Show ${isExpanded ? "less" : "more"}`}
                        icon={isExpanded ? IconCollection.chevronUp : IconCollection.chevronDown}
                        variation="clear"
                        size="small"
                    />
                </div>
            )}
        </div>
    );
};
const baseLogColumns: TableColumn[] = [
    {
        label: "Created at",
        key: "created_at",
        shown: true,
        sortable: true,
        dto: createdAtDto,
    },
    {
        label: "Subject",
        key: "subject",
        shown: true,
        sortable: false,
        // Cap the column width (min/max) so the subject renders on a SINGLE line and
        // ellipsizes to the column width instead of wrapping to a second line, and so
        // a long subject can't push the whole table into a page-level horizontal scroll.
        cellStyle: getRecordColumnCellStyle("subject"),
        dto: ({ subject }: FeRecordLod) => {
            if (!subject) {
                return null;
            }
            // Single-line, CSS-ellipsis truncation that follows the actual rendered
            // column width (the same OverflowTooltip primitive the RecordLabel name
            // cell uses in resource lists): the "…" is applied by CSS to fit the
            // column, and the full subject is revealed on hover ONLY when it is
            // actually truncated. Widening the column reveals more text dynamically.
            return (
                <OverflowTooltip
                    overlay={subject}
                    overlayStyle={{ maxWidth: "800px" }}
                    isOverlapping={false}
                >
                    {subject}
                </OverflowTooltip>
            );
        },
    },
    {
        label: "References",
        headerRenderer: () => <Icon icon={IconCollection.link} />,
        key: "references",
        shown: true,
        sortable: false,
        dto: ({
            id,
            record_log_record_plain_list_record_log_id,
        }: {
            id: string;
            record_log_record_plain_list_record_log_id: FeRecordLogRecord[];
        }) => {
            if (!record_log_record_plain_list_record_log_id?.length) {
                return null;
            }
            if (record_log_record_plain_list_record_log_id.length === 1) {
                return (
                    <ReferenceItem
                        referenceRecord={record_log_record_plain_list_record_log_id[0]}
                    />
                );
            } else {
                return <ReferenceListPopover id={id} />;
            }
        },
    },
    {
        label: "Data",
        key: "data",
        shown: true,
        sortable: false,
        dto: ({ data, id }: FeRecordLod) => {
            return data ? <ExpandableData data={data} uniqueKey={id} /> : null;
        },
    },
];

const recordColumn: TableColumn[] = [
    {
        label: "Record",
        key: "record",
        shown: true,
        sortable: false,
        dto: ({ record_id_record }: FeRecordLod) => {
            if (record_id_record == null) return null;
            const { name, record_type, id } = record_id_record;

            return (
                <RecordLabel
                    key={id}
                    link={getRelativeUrl(getTypedLowerCase(record_type), id)}
                    name={name}
                    iconByType={getTypedLowerCase(record_type)}
                />
            );
        },
    },
];

const allLogColumns = [...baseLogColumns];
allLogColumns.splice(1, 0, ...recordColumn);

export { allLogColumns, baseLogColumns };
